// src/services/api.js
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.a1xpay.com/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  },
  (error) => Promise.reject(error)
);

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// ==========================================
// DOCUMENTATION
// ==========================================
export const documentationAPI = {
  getAPIDocumentation: () => api.get('/payment/doc', { headers: { Accept: 'application/json' } }),
};

// ==========================================
// AUTH
// ==========================================
export const authAPI = {
  register:          (data)               => api.post('/auth/register', data),
  login:             (data)               => api.post('/auth/login', data),
  logout:            ()                   => api.post('/auth/logout'),
  sendOTP:           (email, otp_type)    => api.post('/auth/send-otp', { email, otp_type }),
  verifyOTP:         (email, otp_code, otp_type) => api.post('/auth/verify-otp', { email, otp_code, otp_type }),
  forgotPassword:    (email)              => api.post('/auth/forgot-password', { email }),
  resetPassword:     (data)              => api.post('/auth/reset-password', data),
};

// ==========================================
// USER
// ==========================================
export const userAPI = {
  me:             () => api.get('/user/me'),
  getDashboard:   () => api.get('/user/dashboard'),
  getProfile:     () => api.get('/user/profile'),
  updateProfile:  (data) => api.post('/user/profile/update', data, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  changePassword: (data) => api.post('/user/change-password', data),
};

// ==========================================
// PRODUCT CATALOGUE
// ==========================================
export const productAPI = {
  getProducts: (params = {}) => {
    const qs = new URLSearchParams(
      Object.fromEntries(Object.entries(params).filter(([_, v]) => v !== '' && v != null))
    ).toString();
    return api.get(`/user/products${qs ? `?${qs}` : ''}`);
  },
  getProductDetails: (id) => api.get(`/user/products/${id}`),
  createProduct:     (data) => api.post('/user/products', data),
  updateProduct:     (id, data) => api.put(`/user/products/${id}`, data),
  deleteProduct:     (id) => api.delete(`/user/products/${id}`),
  regenerateToken:   (id) => api.post(`/user/products/${id}/regenerate-token`),
  toggleStatus:      (id) => api.post(`/user/products/${id}/toggle-status`),
  updateStatus:      (id, status) => api.patch(`/user/products/${id}/status`, { status }),
  generateQRCode:    (id, params = { size: 200 }) =>
    api.get(`/user/products/${id}/qr-code?${new URLSearchParams(params).toString()}`),
  getShareLinks:     (id) => api.get(`/user/products/${id}/share-links`),
  duplicateProduct:  (id) => api.post(`/user/products/${id}/duplicate`),
  getProductAnalytics: (id) => api.get(`/user/products/${id}/analytics`),
  getProductTransactions: (id, params = {}) =>
    api.get(`/user/products/${id}/transactions?${new URLSearchParams(params).toString()}`),
  getProductRevenue: (id, params = {}) =>
    api.get(`/user/products/${id}/revenue?${new URLSearchParams(params).toString()}`),
  getProductCustomers: (id, params = {}) =>
    api.get(`/user/products/${id}/customers?${new URLSearchParams(params).toString()}`),
  bulkStatusUpdate: (data) => api.post('/user/products/bulk/status', data),
  bulkDelete:       (data) => api.post('/user/products/bulk/delete', data),
  bulkExport:       (data) => api.post('/user/products/bulk/export', data),
  bulkDuplicate:    (data) => api.post('/user/products/bulk/duplicate', data),
  getProductTemplates: (params = {}) =>
    api.get(`/user/products/templates?${new URLSearchParams(params).toString()}`),
  createProductTemplate: (data) => api.post('/user/products/templates', data),
  createFromTemplate: (templateId, data) => api.post(`/user/products/from-template/${templateId}`, data),
  getCategories: () => api.get('/user/products/categories'),
  createCategory: (data) => api.post('/user/products/categories', data),
  getAnalyticsOverview: (params = {}) =>
    api.get(`/user/analytics/overview?${new URLSearchParams(params).toString()}`),
  getRevenueAnalytics: (params = {}) =>
    api.get(`/user/analytics/revenue?${new URLSearchParams(params).toString()}`),
  getProductsAnalytics: (params = {}) =>
    api.get(`/user/analytics/products?${new URLSearchParams(params).toString()}`),
  getConversionAnalytics: (params = {}) =>
    api.get(`/user/analytics/conversion?${new URLSearchParams(params).toString()}`),
  exportProducts:     (data) => api.post('/user/export/products', data),
  exportTransactions: (data) => api.post('/user/export/transactions', data),
  exportAnalytics:    (data) => api.post('/user/export/analytics', data),
  getExportStatus:    (id) => api.get(`/user/exports/${id}`),
  downloadExport:     (id) => api.get(`/user/exports/${id}/download`, { responseType: 'blob' }),
};

// ==========================================
// WALLET / TRANSACTIONS / WITHDRAWAL / SUPPORT
// ==========================================
export const walletAPI = {
  getBalance:    () => api.get('/wallet'),
  addMoney:      (data) => api.post('/wallet/add-money', data),
  verifyPayment: (data) => api.post('/wallet/verify-payment', data),
  getStatistics: () => api.get('/wallet/statistics'),
};

export const transactionAPI = {
  getTransactions:        (params) => api.get('/transactions', { params }),
  getTransactionDetails:  (id) => api.get(`/transactions/${id}`),
  getTransactionSummary:  () => api.get('/transactions/summary'),
  getChartData:           (months) => api.get('/transactions/chart-data', { params: { months } }),
  exportTransactions:     () => api.get('/transactions/export', { responseType: 'blob' }),
};

export const withdrawalAPI = {
  request:       (data) => api.post('/withdraw/request', data),
  getList:       (params) => api.get('/withdraw/list', { params }),
  getDetails:    (id) => api.get(`/withdraw/${id}`),
  getStatistics: () => api.get('/withdraw/statistics'),
  cancel:        (id) => api.post(`/withdraw/${id}/cancel`),
};

export const supportAPI = {
  createTicket:     (data) => api.post('/ticket/create', data),
  getTickets:       () => api.get('/ticket/list'),
  getTicketDetails: (id) => api.get(`/ticket/${id}`),
  replyToTicket:    (id, data) => api.post(`/ticket/${id}/reply`, data),
};

// ==========================================
// ADMIN
// ==========================================
export const adminAPI = {
  getDashboard: () => api.get('/admin/dashboard'),

  users: {
    getAll:       (params) => api.get('/admin/users', { params }),
    getDetails:   (id) => api.get(`/admin/users/${id}`),
    updateStatus: (id, data) => api.post(`/admin/users/${id}/status`, data),
    approve:      (id) => api.post(`/admin/users/${id}/approve`),
    reject:       (id) => api.post(`/admin/users/${id}/reject`),
    suspend:      (id) => api.post(`/admin/users/${id}/suspend`),
    getTrashed:   () => api.get('/admin/users/trashed'),
    softDelete:   (id) => api.delete(`/admin/users/${id}/soft`),
    restore:      (id) => api.patch(`/admin/users/${id}/restore`),
    hardDelete:   (id) => api.delete(`/admin/users/${id}/hard`),
    loginAs:               (id) => api.post(`/admin/users/${id}/login-as`),
    updateSettlementConfig:(id, data) => api.patch(`/admin/users/${id}/settlement-config`, data),
  },

  transactions: {
    getAll:        (params) => api.get('/admin/transactions', { params }),
    verifyPayment: (id) => api.post(`/admin/transactions/${id}/verify`),
  },

  withdrawals: {
    getAll:  (params) => api.get('/admin/withdrawals', { params }),
    approve: (id) => api.post(`/admin/withdrawals/${id}/approve`),
    reject:  (id, data) => api.post(`/admin/withdrawals/${id}/reject`, data),
  },

  tickets: {
    getAll:       (params) => api.get('/admin/tickets', { params }),
    getDetails:   (id) => api.get(`/admin/tickets/${id}`),
    reply:        (id, data) => api.post(`/admin/tickets/${id}/reply`, data),
    updateStatus: (id, data) => api.post(`/admin/tickets/${id}/status`, data),
  },

  merchantTabs: {
    list:            (search = '') => api.get('/admin/merchant-tabs', { params: { search } }),
    getPermissions:  (id)          => api.get(`/admin/merchants/${id}/tab-permissions`),
    savePermissions: (id, data)    => api.post(`/admin/merchants/${id}/tab-permissions`, data),
  },
};

// ==========================================
// MERCHANT CREDENTIALS / SETTLEMENTS
// ==========================================
export const merchantCredentialsAPI = {
  show:             () => api.get('/merchant/credentials'),
  revealOnce:       () => api.post('/merchant/credentials/reveal'),
  regenerate:       () => api.post('/merchant/api-key/regenerate'),
  updateWebhookUrl: (url) => api.patch('/merchant/webhook-url', { webhook_url: url }),
};

export const shopifyConfigAPI = {
  get:  ()       => api.get('/merchant/shopify-config'),
  save: (data)   => api.patch('/merchant/shopify-config', data),
};

export const wordpressConfigAPI = {
  get:  ()       => api.get('/merchant/wordpress-config'),
  save: (data)   => api.patch('/merchant/wordpress-config', data),
};

export const merchantSettlementsAPI = {
  list:         (params) => api.get('/user/settlements', { params }),
  show:         (id)     => api.get(`/user/settlements/${id}`),
  pendingToday: ()       => api.get('/user/settlements/pending-today'),
};

// ==========================================
// ADMIN — MERCHANT MANAGEMENT, SETTLEMENTS, WEBHOOKS
// ==========================================
export const adminMerchantsAPI = {
  list:              (params) => api.get('/admin/merchants', { params }),
  show:              (id)     => api.get(`/admin/merchants/${id}`),
  suspend:           (id)     => api.patch(`/admin/merchants/${id}/suspend`),
  activate:          (id)     => api.patch(`/admin/merchants/${id}/activate`),
  updateMarkup:      (id, body) => api.patch(`/admin/merchants/${id}/markup`, body),
  rotateCredentials: (id)     => api.post(`/admin/merchants/${id}/rotate-credentials`),

  // Bank details
  getBankDetails:    (userId) => api.get(`/admin/users/${userId}/bank-details`),
  updateBankDetails: (userId, body) => api.put(`/admin/users/${userId}/bank-details`, body),
  deleteBankDetails: (userId) => api.delete(`/admin/users/${userId}/bank-details`),

  // Transaction limit
  updateTxnLimit:    (userId, limit) => api.patch(`/admin/users/${userId}/transaction-limit`, { transaction_limit: limit }),

  // Partner Gateway IDs
  updatePartnerIds:  (userId, body) => api.patch(`/admin/users/${userId}/partner-ids`, body),
};

export const userKycAPI = {
  status: () => api.get('/user/kyc/status'),
  submit: (data) => api.post('/user/kyc/submit', data, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
};

export const adminKycAPI = {
  fields:       ()         => api.get('/admin/kyc/fields'),
  list:         (params)   => api.get('/admin/kyc/forms', { params }),
  create:       (body)     => api.post('/admin/kyc/forms', body),
  show:         (id)       => api.get(`/admin/kyc/forms/${id}`),
  updateStatus: (id, body) => api.patch(`/admin/kyc/forms/${id}/status`, body),
  destroy:      (id)       => api.delete(`/admin/kyc/forms/${id}`),
};

export const adminSettlementsAPI = {
  list:         (params) => api.get('/admin/settlements', { params }),
  show:         (id)     => api.get(`/admin/settlements/${id}`),
  create:       (body)   => api.post('/admin/settlements', body),
  pendingToday: ()       => api.get('/admin/settlements/pending-today'),
  confirm:      (body)   => api.post('/admin/settlements/confirm', body),
};

export const adminWebhookLogsAPI = {
  list:   (params) => api.get('/admin/webhook-logs', { params }),
  show:   (id)     => api.get(`/admin/webhook-logs/${id}`),
  replay: (id)     => api.post(`/admin/webhook-logs/${id}/replay`),
};

export const adminGatewayAPI = {
  getGateways:  ()       => api.get('/admin/gateway'),
  setActive:    (name)   => api.post('/admin/gateway/set-active', { gateway: name }),
};

// ==========================================
// PAYMENT LINKS
// ==========================================
export const otpAPI = {
  send:   (phone)       => api.post('/auth/send-otp',   { phone }),
  verify: (phone, otp)  => api.post('/auth/verify-otp', { phone, otp }),
};

export const paymentLinkAPI = {
  list:   ()                   => api.get('/merchant/payment-links'),
  create: (data)               => api.post('/merchant/payment-links', data),
  share:  (token, data)        => api.post(`/merchant/payment-links/${token}/share`, data),
  get:    (token)              => api.get(`/payment-link/${token}`),
  pay:    (token, data)        => api.post(`/payment-link/${token}/pay`, data),
};

// ==========================================
// ADMIN — BUSINESS PARTNERS
// ==========================================
export const adminBusinessPartnerAPI = {
  list:           (params) => api.get('/admin/business-partners', { params }),
  create:         (data)   => api.post('/admin/business-partners', data),
  show:           (id)     => api.get(`/admin/business-partners/${id}`),
  update:         (id, data) => api.put(`/admin/business-partners/${id}`, data),
  deactivate:     (id)     => api.delete(`/admin/business-partners/${id}`),
  regenerateCode: (id)     => api.post(`/admin/business-partners/${id}/regenerate-code`),
};

// ==========================================
// BUSINESS PARTNER PANEL
// ==========================================
export const bpAPI = {
  dashboard:       ()           => api.get('/bp/dashboard'),
  profile:         ()           => api.get('/bp/profile'),
  changePassword:  (data)       => api.post('/bp/profile/change-password', data),
  merchants:       (params)     => api.get('/bp/merchants', { params }),
  merchantShow:    (id)         => api.get(`/bp/merchants/${id}`),
  transactions:    (params)     => api.get('/bp/transactions', { params }),
  kycLinks:        (params)     => api.get('/bp/kyc-links', { params }),
  createKycLink:   (data)       => api.post('/bp/kyc-links', data),
  settlements:     (params)     => api.get('/bp/settlements', { params }),
};

// ==========================================
// POS MERCHANT
// ==========================================
export const posAPI = {
  dashboard:     ()           => api.get('/pos/dashboard'),
  initPayment:   (body)       => api.post('/pos/init-payment', body),
  checkStatus:   (txnId)      => api.get(`/pos/status/${txnId}`),
  cancelPayment: (txnId)      => api.put(`/pos/cancel/${txnId}`),
  transactions:  (params)     => api.get('/pos/transactions', { params }),
};

export const adminEDCAPI = {
  listTerminals:    (params)    => api.get('/admin/edc/terminals', { params }),
  assignTerminal:   (body)      => api.post('/admin/edc/terminals', body),
  updateTerminal:   (id, body)  => api.patch(`/admin/edc/terminals/${id}`, body),
  allTransactions:  (params)    => api.get('/admin/edc/transactions', { params }),
};

export default api;
